import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Users, Building, Shield, ArrowLeft, MessageCircle, Clock, CheckCircle } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-slate-200 bg-white" data-testid="faq-item">
      <button
        onClick={onClick}
        className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition"
        data-testid="faq-question"
      >
        <span className="font-semibold text-slate-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="text-rose-600 flex-shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-slate-400 flex-shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6" data-testid="faq-answer">
          <div className="prose prose-sm prose-slate max-w-none">
            {answer}
          </div>
        </div>
      )}
    </div>
  );
};

const FAQSection = ({ title, subtitle, icon: Icon, items, color }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="mb-16">
      <div className="flex items-start gap-4 mb-8">
        <div className={`w-12 h-12 ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="text-white" size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          <p className="text-slate-600 mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((item, index) => (
          <FAQItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>
    </div>
  );
};

export default function FAQPage() {
  const navigate = useNavigate();
  const trabajadoresItems = [
    {
      question: "Me han notificado una incapacidad permanente total. ¿Mi empresa puede despedirme ya?",
      answer: (
        <div className="space-y-4">
          <p>No automáticamente. Antes de la Ley 2/2025, el reconocimiento de una incapacidad permanente total era causa automática de extinción del contrato. Eso ha cambiado.</p>
          <p>Ahora tienes <strong>10 días naturales</strong> desde que recibes la notificación del INSS para comunicar por escrito a tu empresa que quieres mantener tu relación laboral. Si lo haces, la empresa queda obligada a explorar si puede adaptarte el puesto o reubicarte en otro compatible con tus limitaciones. Solo si eso resulta imposible podrá extinguir el contrato, y tendrá que justificarlo por escrito con un informe técnico del servicio de prevención.</p>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
            <p className="font-semibold text-amber-800">Importante:</p>
            <p className="text-amber-700">Si no comunicas nada en esos 10 días, la empresa puede iniciar el proceso de extinción. El plazo corre desde la notificación, no desde que tú la recibas. No lo dejes para después.</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Puedo cobrar la pensión de incapacidad y volver a trabajar al mismo tiempo?",
      answer: (
        <div className="space-y-4">
          <p>En general, no de forma simultánea. La Ley 2/2025 introduce una regla clara: si te reincorporas a un puesto adaptado en la misma empresa, la pensión de incapacidad permanente queda <strong>suspendida</strong> mientras dure esa reincorporación. Trabajo y pensión no son compatibles al mismo tiempo en este escenario.</p>
          <p>Si más adelante el contrato se extingue por cualquier causa, la pensión se reactiva. Y si la reincorporación no llega a producirse porque la empresa no puede adaptarte el puesto, mantienes el derecho a la pensión íntegra.</p>
          <p className="text-slate-500 text-sm">La compatibilidad exacta entre trabajo y prestación está pendiente de desarrollo reglamentario que el Gobierno debe completar en el plazo marcado por la propia ley. Si tu situación es compleja, conviene consultar antes de tomar ninguna decisión.</p>
        </div>
      )
    },
    {
      question: "Llevo 8 meses de baja y voy a volver. ¿Tiene que hacerme la empresa un reconocimiento médico?",
      answer: (
        <div className="space-y-4">
          <p>Sí. Tras una baja de larga duración, el servicio de prevención debe realizar un reconocimiento médico de reincorporación. No es un trámite decorativo: sirve para evaluar si eres <strong>apto, no apto o apto con limitaciones</strong> para el puesto que vas a ocupar.</p>
          <p>Si el resultado incluye restricciones (no levantar cargas, no turnos nocturnos, no determinadas posturas), la empresa está obligada a tenerlas en cuenta. No puede simplemente ignorarlas y mandarte a hacer lo mismo de antes.</p>
          <div className="bg-slate-50 p-4 border border-slate-200">
            <p className="font-semibold mb-2">Lo que tú puedes hacer:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Pide por escrito que te confirmen si habrá reconocimiento médico antes de tu vuelta efectiva.</li>
              <li>Lleva los informes médicos que expliquen tus limitaciones funcionales (no hace falta entregar todo tu historial clínico).</li>
              <li>Documenta cualquier presión para que asumas tareas que tu médico ha descartado.</li>
            </ul>
          </div>
          <p className="text-sm text-slate-600">La confidencialidad de tus datos médicos está protegida por ley. La empresa solo puede conocer la conclusión de aptitud, no el diagnóstico ni el tratamiento.</p>
        </div>
      )
    },
    {
      question: "Mi empresa dice que no puede adaptarme el puesto. ¿Tengo que aceptarlo?",
      answer: (
        <div className="space-y-4">
          <p>No necesariamente. La empresa tiene que <strong>demostrar</strong> que la adaptación es imposible o supone una carga excesiva. Esa demostración tiene que ser real: debe contar con un informe técnico del servicio de prevención que lo avale y con una comunicación escrita que motive por qué no es posible el ajuste ni la reubicación en otro puesto compatible.</p>
          <p>Una empresa que simplemente dice "no podemos" sin haber evaluado las alternativas ni contar con ese informe técnico está en una posición jurídica muy débil. En esos casos, la extinción del contrato puede ser impugnable como <strong>despido nulo</strong>, con las consecuencias que ello conlleva: readmisión obligatoria o indemnización reforzada más los salarios de tramitación.</p>
          <div className="bg-rose-50 border-l-4 border-rose-500 p-4">
            <p className="text-rose-800 font-semibold">Antes de firmar nada, consulta con un abogado especialista. Una firma mal dada en el momento equivocado puede costarte derechos que difícilmente recuperarás después.</p>
          </div>
        </div>
      )
    }
  ];

  const empresasItems = [
    {
      question: "Un trabajador con incapacidad permanente quiere volver. ¿Cuánto tiempo tenemos para responder?",
      answer: (
        <div className="space-y-4">
          <p><strong>Tres meses.</strong> Desde que el trabajador comunica por escrito su voluntad de mantener la relación laboral, la empresa dispone de un máximo de tres meses para evaluar las posibilidades de adaptación o reubicación y tomar una decisión.</p>
          <p>Durante ese tiempo, el contrato queda suspendido con reserva de puesto. La empresa debe actuar:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Solicitar al servicio de prevención una evaluación de aptitud y de adaptaciones posibles</li>
            <li>Identificar vacantes compatibles en el grupo profesional</li>
            <li>Informar al Comité de Empresa o Delegados de Prevención</li>
          </ul>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
            <p className="font-semibold text-red-800">Consecuencia del silencio:</p>
            <p className="text-red-700">Si los tres meses transcurren sin que la empresa haya resuelto el protocolo, la extinción posterior puede calificarse de despido nulo (art. 55.5 ET reformado por la Ley 2/2025). No actuar no es una opción neutral.</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Qué pasa si somos una empresa pequeña y no podemos asumir la adaptación del puesto?",
      answer: (
        <div className="space-y-4">
          <p>La Ley 2/2025 reconoce que la carga puede ser desproporcionada para empresas pequeñas. Para empresas de <strong>menos de 25 trabajadores</strong>, se considera carga excesiva cuando el coste de la adaptación supera el mayor de estos dos valores:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>La indemnización por despido improcedente del trabajador en cuestión.</li>
            <li>Seis mensualidades de su salario bruto.</li>
          </ul>
          <p className="text-sm text-slate-600">No se computan en ese cálculo las ayudas públicas disponibles ni las bonificaciones por contratación de personas con discapacidad, que pueden reducir significativamente el coste real.</p>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
            <p className="text-amber-800"><strong>Pero atención:</strong> que el coste sea excesivo no te exime de hacer el proceso. Tienes que acreditarlo con un informe técnico del servicio de prevención y motivarlo por escrito. Sin ese informe, la extinción del contrato puede ser impugnada con éxito.</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Qué tres errores cometen más las empresas en estas situaciones y qué consecuencias tienen?",
      answer: (
        <div className="space-y-4">
          <div className="space-y-4">
            <div className="border-l-4 border-slate-300 pl-4">
              <p className="font-semibold">1. Tratar la reincorporación como un alta administrativa.</p>
              <p className="text-slate-600">Devolver al trabajador a su puesto sin reconocimiento médico previo, sin evaluar si sus limitaciones son compatibles con las tareas y sin informar al mando directo. <span className="text-red-600">Consecuencia: nuevo daño, nueva baja, nueva responsabilidad.</span></p>
            </div>
            <div className="border-l-4 border-slate-300 pl-4">
              <p className="font-semibold">2. Ignorar el informe de aptitud.</p>
              <p className="text-slate-600">El servicio de prevención emite un "apto con restricciones" y la empresa sigue asignando las mismas cargas y los mismos turnos. <span className="text-red-600">Esa contradicción documentada es oro para una demanda posterior.</span></p>
            </div>
            <div className="border-l-4 border-slate-300 pl-4">
              <p className="font-semibold">3. Dejar solo al mando intermedio.</p>
              <p className="text-slate-600">El jefe directo recibe un mensaje vago del tipo "trátalo con cuidado" sin criterios, sin formación, sin soporte. <span className="text-red-600">Cuando hay incidentes, la empresa alega que "el mando lo gestionó mal". Los tribunales no suelen aceptar ese argumento.</span></p>
            </div>
          </div>
          <p className="font-medium mt-4">La excusa del "no sabía" ya no funciona en los juzgados. La clave es documentar bien cada paso del protocolo, desde el primer contacto hasta el seguimiento posterior al reingreso.</p>
        </div>
      )
    },
    {
      question: "¿Tenemos que consultar a los representantes de los trabajadores en este proceso?",
      answer: (
        <div className="space-y-4">
          <p><strong>Sí.</strong> La consulta a los Delegados de Prevención o al Comité de Empresa es un paso obligatorio dentro del proceso de evaluación de ajustes razonables. No es una formalidad opcional: es un requisito cuya omisión puede comprometer la validez de la extinción posterior si llega a producirse.</p>
          <p>El momento de la consulta es durante la Fase II del protocolo (los primeros dos meses desde la comunicación del trabajador), mientras el servicio de prevención está evaluando las adaptaciones posibles. La empresa debe informar de las opciones analizadas y recibir el posicionamiento de la representación legal de los trabajadores antes de tomar la decisión final.</p>
          <p className="text-sm text-slate-600">Documentar esa consulta y su resultado forma parte del expediente que puede necesitarse si la decisión de la empresa es impugnada.</p>
        </div>
      )
    }
  ];

  const prevencionItems = [
    {
      question: "¿Qué debe contener el informe de aptitud en una reincorporación tras baja larga?",
      answer: (
        <div className="space-y-4">
          <p>Un informe de aptitud útil no es el que dice <em>Apto</em> o <em>No Apto</em>. Es el que <strong>traduce la situación clínica del trabajador en limitaciones operativas concretas</strong> referidas a las tareas reales del puesto.</p>
          <p>Debe responder a preguntas como:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>¿Qué tareas no puede realizar?</li>
            <li>¿En qué condiciones?</li>
            <li>¿Durante cuánto tiempo?</li>
            <li>¿Hay alternativas de adaptación ergonómica, horaria o de contenido?</li>
            <li>¿Se recomienda una reincorporación progresiva?</li>
          </ul>
          <p>Un informe genérico que no baja a ese nivel de detalle no protege al trabajador, no orienta a la empresa y no protege al técnico que lo firma. Además, desde la <strong>STS 563/2025</strong>, ese tipo de informes puede ser factor directo de responsabilidad penal del técnico.</p>
          <p className="text-sm text-slate-600">La confidencialidad médica limita lo que el SPA puede comunicar a la empresa. Pero esa limitación no impide ser preciso en las limitaciones funcionales operativas. La precisión no vulnera la privacidad: la vaguedad simplemente no sirve.</p>
        </div>
      )
    },
    {
      question: "¿Qué responsabilidad tiene el SPA si la empresa extingue el contrato sin protocolo previo?",
      answer: (
        <div className="space-y-4">
          <p>El servicio de prevención ajeno es sujeto responsable de infracciones según el artículo 2.9 de la LISOS. Si la empresa extingue el contrato sin el informe técnico previo del SPA que la Ley 2/2025 exige, y ese informe no existe porque el SPA no lo emitió, el SPA puede compartir la responsabilidad por ese incumplimiento.</p>
          <p>Además, si el SPA sí fue requerido y emitió un informe deficiente o genérico que no analizó realmente las opciones de adaptación, ese informe puede usarse en sede judicial como prueba de una actuación negligente.</p>
          <div className="bg-red-50 border border-red-200 p-4">
            <p className="text-red-800">Las consecuencias van desde la responsabilidad civil por daños hasta sanciones administrativas graves (art. 12.16 LISOS) y, en los casos más graves, <strong>responsabilidad penal del técnico firmante</strong> (art. 316 CP, STS 563/2025).</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Qué dice exactamente la STS 563/2025 y por qué importa a los técnicos de PRL?",
      answer: (
        <div className="space-y-4">
          <p>La Sentencia del Tribunal Supremo 563/2025, de 19 de junio de 2025 (Sala de lo Penal), condenó a una técnica de prevención de riesgos laborales por un delito contra los derechos de los trabajadores tipificado en el artículo 316 del Código Penal, tras un accidente grave.</p>
          <p>La doctrina establecida es clara: <strong>la responsabilidad penal del artículo 316 CP no es exclusiva del empresario</strong>. Alcanza a quien, por su función, su formación y su posición en la organización, tenga el deber de garantizar la seguridad y lo incumpla. Una evaluación de riesgos genérica, no adaptada al centro real de trabajo ni a las tareas reales de los trabajadores, puede ser factor directo de esa responsabilidad.</p>
          <div className="bg-slate-100 p-4 border border-slate-200">
            <p className="font-semibold mb-2">La sentencia establece que una delegación válida requiere tres condiciones:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Que sea formal y esté documentada</li>
              <li>Que el delegado tenga la cualificación necesaria</li>
              <li>Que cuente con los medios suficientes para cumplir su función</li>
            </ol>
          </div>
          <p className="font-medium">No firmar el documento no exime de responsabilidad si las funciones se ejercen materialmente. El criterio del tribunal es funcional, no meramente formal.</p>
        </div>
      )
    },
    {
      question: "¿Qué documentación mínima debe dejar el SPA tras gestionar una reincorporación?",
      answer: (
        <div className="space-y-4">
          <p>El rastro documental de una reincorporación bien gestionada debe incluir, como mínimo:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span><strong>Reconocimiento médico de reincorporación</strong> con conclusión de aptitud y limitaciones funcionales concretas (no genéricas).</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span><strong>Evaluación de riesgos actualizada</strong> del puesto al que se va a reincorporar el trabajador, con las adaptaciones propuestas.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span><strong>Informe técnico de adaptaciones posibles</strong>, con análisis de las alternativas consideradas y su viabilidad.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span><strong>Acreditación de la comunicación</strong> a la empresa y, si aplica, de la consulta con los delegados de prevención.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span><strong>Registro del seguimiento posterior</strong> al reingreso, con al menos una revisión en las primeras semanas.</span>
            </li>
          </ul>
          <p className="text-slate-600 font-medium mt-4">Todo ese expediente debe poder presentarse ante una Inspección de Trabajo o ante un juzgado sin ambigüedades. Lo que no está documentado, a efectos prácticos, no existió.</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-back-home">
            <ArrowLeft size={18} className="text-slate-500" />
            <span className="text-sm text-slate-600">Volver a inicio</span>
          </Link>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" 
              alt="PrevenciónLegal @tecnoabogado" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-rose-600 text-white text-xs font-bold uppercase tracking-wider mb-6">
            Preguntas Frecuentes
          </span>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            FAQ Reincorporaciones tras baja larga
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Todo lo que deben saber trabajadores, empresas y servicios de prevención sobre la Ley 2/2025.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-16 px-6">
        <FAQSection
          title="Para trabajadores"
          subtitle="Tienes derechos. Y ahora también tienes plazos."
          icon={Users}
          items={trabajadoresItems}
          color="bg-blue-600"
        />

        <FAQSection
          title="Para empresas y RRHH"
          subtitle="El plazo ya corre. ¿Tienes un protocolo o estás improvisando?"
          icon={Building}
          items={empresasItems}
          color="bg-amber-600"
        />

        <FAQSection
          title="Para servicios de prevención"
          subtitle="Tu informe tiene más peso del que crees."
          icon={Shield}
          items={prevencionItems}
          color="bg-rose-600"
        />

        {/* CTA Section */}
        <section className="mt-16 bg-slate-900 text-white p-12 text-center" data-testid="faq-cta">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            ¿Tu situación no encaja en ninguna de estas respuestas?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Las reincorporaciones tras baja larga son complejas y cada caso tiene sus propias variables. Analizamos el tuyo sin compromiso.
          </p>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'contacto' } })}
            className="inline-flex items-center gap-2 bg-rose-600 text-white px-8 py-4 font-bold hover:bg-rose-700 transition"
            data-testid="faq-contact-btn"
          >
            <MessageCircle size={20} />
            Consulta con un especialista
          </button>
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <CheckCircle size={16} />
              Sin compromiso
            </span>
            <span className="flex items-center gap-2">
              <Clock size={16} />
              Respuesta en 24 h
            </span>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>© 2026 Vilaplana Tecnoabogado. Todos los derechos reservados.</p>
          <div className="flex gap-6">
            <a href="https://www.tecnoabogado.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">tecnoabogado.com</a>
            <a href="https://www.vilaplanaabogados.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">vilaplanaabogados.com</a>
            <a href="https://www.despido.net" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">despido.net</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
